import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import { DataTable } from "@frui.ts/dataviews/dist";
import type { ColumnDefinition, PropertyKey, ValueRenderProps } from "@frui.ts/dataviews/dist/dataTypes";
import { registerView, View } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views";
import type ExemptionResponse from "entities/exemptionResponse";
import { observer, Observer } from "mobx-react-lite";
import React from "react";
import { formatDate, formatDateTime, formatLicensePlate, translateGeneral } from "utils/localizations";
import ExemptionsViewModel from "viewModels/callCenter/search/exemptions/exemptionsViewModel";
import Paginator from "views/common/paginator";
import ExemptionActionMenu from "./exemptionActionMenu";
import Tooltip from "@edaz/edazkit/Tooltip";
import { emptyValue } from "utils/emptyValue";

const ExemptionsView: ViewComponent<ExemptionsViewModel> = observer(({ vm }) => {
  function simpleValueFormatter(props: ValueRenderProps<ExemptionResponse, any, PropertyKey<ExemptionResponse>>) {
    if (!props.value) {
      return emptyValue;
    }
    return props.value;
  }

  const headerClassName = "py-4 text-wrap align-middle";
  const headerLeftAlignClassName = "py-4 text-left text-nowrap";
  const wrapClassName = "text-wrap";
  const noWrapClassName = "text-nowrap";
  const columns: ColumnDefinition<ExemptionResponse>[] = [
    {
      title: vm.translateAttribute("vehicle"),
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      cellProps: _ => ({ style: { minWidth: 180 } }),
      valueFormatter: props => {
        const newLicensePlate = props.item.vehicle.licensePlate;
        const originalLicensePlate = vm.filter.licensePlate;

        const licensePlateChangedInformation = originalLicensePlate && newLicensePlate !== originalLicensePlate;
        if (licensePlateChangedInformation) {
          const submissionDateText = props.item.vehicleRegistrationChangeSubmissionDate
            ? vm
                .translate("submissionDate")
                .replace("%date%", formatDate(props.item.vehicleRegistrationChangeSubmissionDate) || emptyValue)
            : "";

          const licensePlateChangeTooltip = vm
            .translate("licensePlateChanged")
            .replace("%originalLicensePlate%", originalLicensePlate)
            .replace("%newLicensePlate%", newLicensePlate)
            .replace("%submissionDate%", submissionDateText);

          return (
            <Tooltip id="license-plate-tooltip" className="license-plate-tooltip" tooltip={licensePlateChangeTooltip ?? ""}>
              <span className="font-weight-bold text-nowrap text-secondary link-underline">
                <span>{formatLicensePlate(props.item.vehicle)}</span>
              </span>
            </Tooltip>
          );
        }
        return formatLicensePlate(props.item.vehicle);
      },
    },
    {
      title: vm.translateAttribute("validSince"),
      property: "validSince",
      cellClassName: wrapClassName,
      headerClassName: headerLeftAlignClassName,
      cellProps: _ => ({ style: { minWidth: 140 } }),
      valueFormatter: props => (props.value ? formatDateTime(props.value) : emptyValue),
    },
    {
      title: vm.translateAttribute("validUntil"),
      property: "validUntil",
      cellClassName: wrapClassName,
      headerClassName: headerLeftAlignClassName,
      cellProps: _ => ({ style: { minWidth: 140 } }),
      valueFormatter: props => (props.value ? formatDateTime(props.value) : emptyValue),
    },
    {
      title: vm.translateAttribute("creationRecordId"),
      property: "creationRecordId",
      valueFormatter: simpleValueFormatter,
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
    },
    {
      title: vm.translateAttribute("cancellationRecordId"),
      property: "cancellationRecordId",
      valueFormatter: simpleValueFormatter,
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
    },
    {
      title: vm.translateAttribute("vehicleRegistrationChangeRecordId"),
      valueFormatter: props => {
        return props.item.vehicleRegistrationChangeRecordId ?? emptyValue;
      },
      property: "vehicleRegistrationChangeRecordId",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
    },
    {
      title: vm.translateAttribute("exemptionReasonId"),
      property: "exemptionReasonId",
      valueFormatter: props => {
        return <span dangerouslySetInnerHTML={{ __html: vm.translateExemptionReasonId(props.value) }}></span>;
      },
      cellProps: props => ({ style: { maxWidth: 500, minWidth: 300 } }),
      headerClassName: headerClassName,
    },
    {
      title: vm.translateAttribute("creationMethod"),
      property: "creationMethod",
      valueFormatter: props => vm.translateExemptionRegistrationMethod(props.value),
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
    },
    {
      title: vm.translateAttribute("cancellationMethod"),
      property: "cancellationMethod",
      valueFormatter: props => vm.translateExemptionRegistrationMethod(props.value),
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
    },
    {
      cellClassName: "text-align-end",
      headerClassName: headerClassName,
      valueFormatter: props => <ExemptionActionMenu vm={vm} exemption={props.item} />,
    },
  ];

  return (
    <>
      <LoaderOverlay loading={vm.loading}>
        <DataTable
          headerRowClassName="bg-white"
          columns={columns}
          items={vm.items}
          rowProps={item => {
            let classNames = {
              className: vm.rowColor(item.validSince, item.validUntil),
            };
            if (item === vm.openItem) {
              classNames = {
                className: `${classNames.className} table-active`,
              };
            }

            return classNames;
          }}
          itemKey="exemptionId"
          className="mt-n4 table"
        />
        {vm.loading && vm.items.length === 0 && (
          <div className="bg-light mt-n4 mb-4 px-4 py-3 border-bottom text-muted">{translateGeneral("loading")}</div>
        )}
        {!vm.loading && vm.items.length === 0 && (
          <div className="bg-light mt-n4 mb-4 px-4 py-3 border-bottom text-muted">{translateGeneral("empty")}</div>
        )}
        <Paginator
          className="justify-content-center mt-4 mb-2"
          pagingInfo={vm.currentPaging}
          filter={vm.pagingFilter}
          onPageChanged={() => vm.loadData()}
        />
      </LoaderOverlay>
      <Observer>{() => <View vm={vm.activeChild} />}</Observer>
    </>
  );
});

registerView(ExemptionsView, ExemptionsViewModel);
